import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';
import { SessionService } from 'src/app/core/backend-adapter/session.service';

@Injectable({ providedIn: 'root' })
export class AbpLocationsFilterService {
  private baseUrl ='https://io.renderseo.com/access/filters';
  private readonly appleCtasDataFromFiltersUrl = 'https://io.renderseo.com/apple/ctas-data-from-filters';
    /*window.location.hostname.includes('localhost') || window.location.hostname.includes('dev')
      ? 'https://dev-io.renderseo.com/access/filters'
      : 'https://io.renderseo.com/access/filters';*/
      

  // Cache for locations data
  private filtersCache: { [key: string]: { data: any; timestamp: number } } = {};
  private cacheTimeout = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient, private sessionService: SessionService) {}

  getFilters(accountId: number, userId: number, useCache: boolean = true): Promise<any> {
    const cacheKey = `${accountId}_${userId}`;
    console.log(`🔍 getFilters called - accountId: ${accountId}, userId: ${userId}, cacheKey: ${cacheKey}, useCache: ${useCache}`);

    // Check cache first
    if (useCache && this.filtersCache[cacheKey]) {
      const cached = this.filtersCache[cacheKey];
      const now = Date.now();
      const age = now - cached.timestamp;
      // Return cached data if it's still valid
      if (age < this.cacheTimeout) {
        console.log(`✅ Using cached locations data (age: ${Math.round(age / 1000)}s, timeout: ${this.cacheTimeout / 1000}s)`);
        console.log(`📦 Cache contents: ${Object.keys(this.filtersCache).length} entries`);
        return Promise.resolve(cached.data);
      } else {
        console.log(`⚠️ Cache expired (age: ${Math.round(age / 1000)}s, timeout: ${this.cacheTimeout / 1000}s), fetching fresh data`);
        // Cache expired, remove it
        delete this.filtersCache[cacheKey];
      }
    } else if (useCache) {
      console.log(`ℹ️ No cache found for key "${cacheKey}", fetching locations data from API`);
      console.log(`📦 Current cache keys: ${Object.keys(this.filtersCache).join(', ') || 'none'}`);
    } else {
      console.log(`🔄 Cache disabled (useCache=false), fetching fresh data`);
    }

    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000, // ~18s
    };

    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');

    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);

    console.log('=== AUTH TOKEN (getFilters) ===');
    console.log('JWT Payload:', payloadObj);
    console.log('JWT Token:', jwtToken);
    console.log('Authorization Header:', `Authorization: ${jwtToken}`);
    console.log(`🌐 Fetching locations from API (cacheKey: ${cacheKey})...`);
    return this.http.get<any>(this.baseUrl, { headers }).toPromise().then((data) => {
      // Cache the result
      this.filtersCache[cacheKey] = {
        data: data,
        timestamp: Date.now()
      };
      console.log(`✅ Locations data fetched and cached (cacheKey: ${cacheKey})`);
      console.log(`📦 Total cache entries: ${Object.keys(this.filtersCache).length}`);
      return data;
    });
  }

  /**
   * Call GET /access/filters/locations with auth token and console.log the response.
   * Use for debugging the locations API output.
   */
  logLocationsApiOutput(): void {
    const accountId = this.sessionService.effAccount_id;
    const userId = this.sessionService.loginObj?._id;
    if (!accountId || !userId) {
      console.warn('[access/filters/locations] Cannot log output: missing accountId or userId');
      return;
    }
    const url = `${this.baseUrl}/locations`;
    console.log('[access/filters/locations] Fetching with auth token...', url);
    this.getFilteredLocations(accountId, userId, {})
      .then((data) => {
        console.log('[access/filters/locations] API output:', data);
        console.log('[access/filters/locations] API output (JSON):', JSON.stringify(data, null, 2));
      })
      .catch((err) => {
        console.error('[access/filters/locations] API error:', err);
      });
  }

  // Method to preload filters (checks cache first, only fetches if cache is missing or expired)
  preloadFilters(accountId: number, userId: number): Promise<any> {
    return this.getFilters(accountId, userId, true);
  }

  /**
   * Builds the URL query string for GET /access/filters/locations and other endpoints
   * that expect the same filter encoding (e.g. /apple/ctas-data-from-filters).
   *
   * Omits keys whose values are null or empty after trim. Encodes keys and values with
   * encodeURIComponent, then restores comma literals in values (same as legacy locations call).
   *
   * @param queryParams Flat string map of filter keys to values (e.g. operand, storecode, city).
   * @returns Query string without a leading `?`, or empty string when there are no pairs.
   */
  buildFiltersLocationsQueryString(queryParams: { [key: string]: string }): string {
    return Object.keys(queryParams)
      .filter((key) => queryParams[key] != null && queryParams[key] !== '')
      .map((key) => {
        const encodedKey = encodeURIComponent(key);
        const value = String(queryParams[key]).trim();
        const encodedValue = encodeURIComponent(value).replace(/%2C/g, ',');
        return `${encodedKey}=${encodedValue}`;
      })
      .join('&');
  }

  // Method to get filtered locations based on query parameters
  getFilteredLocations(accountId: number, userId: number, queryParams: { [key: string]: string }): Promise<any> {
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000, // ~18s
    };

    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);

    console.log('=== AUTH TOKEN (getFilteredLocations) ===');
    console.log('JWT Payload:', payloadObj);
    console.log('JWT Token:', jwtToken);
    console.log('Authorization Header:', `Authorization: ${jwtToken}`);

    const queryString = this.buildFiltersLocationsQueryString(queryParams);

    const locationsEndpoint = `${"https://io.renderseo.com/access/filters"}/locations`;
    const url = queryString ? `${locationsEndpoint}?${queryString}` : locationsEndpoint;
    
    console.log('=== API REQUEST ===');
    console.log('Full URL:', url);
    console.log('Query params object:', queryParams);
    console.log('Request Headers:', {
      'Authorization': jwtToken
    });

    return this.http.get<any>(url, { headers }).toPromise().then((data) => {
      return data;
    });
  }

  /**
   * GET /apple/ctas-data-from-filters with the same query string as /access/filters/locations.
   * Response includes `locations` and `ctas` (with `cta_summary`, `account_ident`, `total_locations`).
   *
   * @param accountId Account id for JWT `account_id` claim.
   * @param userId User id for JWT `sub` claim.
   * @param queryParams Filter map (operand, storecode, city, …); encoded via {@link buildFiltersLocationsQueryString}.
   * @returns Parsed JSON body from the Apple endpoint.
   */
  getCtasDataFromFilters(
    accountId: number,
    userId: number,
    queryParams: { [key: string]: string }
  ): Promise<any> {
    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };

    const jwtToken = generateJWTToken(payloadObj, '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a');
    const headers = new HttpHeaders().set('Authorization', `${jwtToken}`);
    const queryString = this.buildFiltersLocationsQueryString(queryParams);
    const url = queryString
      ? `${this.appleCtasDataFromFiltersUrl}?${queryString}`
      : this.appleCtasDataFromFiltersUrl;

    return this.http.get<any>(url, { headers }).toPromise();
  }
}
